type List = {
  _id: string;
  title: string;
  cards: string[];
  sprintId?: string; // The sprint this list belongs to (null/undefined = backlog)
};

type ListsByIdState = Record<string, List>;

type ListsByIdAction =
  | { type: "SET_LISTS"; payload: Record<string, List> }
  | { type: "ADD_CARD"; payload: { listId: string; cardId: string } }
  | {
      type: "MOVE_CARD";
      payload: {
        oldCardIndex: number;
        newCardIndex: number;
        sourceListId: string;
        destListId: string;
      };
    }
  | { type: "DELETE_CARD"; payload: { cardId: string; listId: string } }
  | { type: "ADD_LIST"; payload: { listId: string; listTitle: string } }
  | { type: "CHANGE_LIST_TITLE"; payload: { listId: string; listTitle: string } }
  | { type: "DELETE_LIST"; payload: { listId: string } }
  | { type: "SET_LIST_SPRINT"; payload: { listId: string; sprintId: string | null } };

const listsById = (state: ListsByIdState = {}, action: ListsByIdAction): ListsByIdState => {
  switch (action.type) {
    case "SET_LISTS": {
      return {
        ...state,
        ...action.payload
      };
    }
    case "ADD_CARD": {
      const { listId, cardId } = action.payload;
      // Check if list exists before adding card
      if (!state[listId]) {
        console.warn(`ADD_CARD: List ${listId} not found in state`);
        return state;
      }
      return {
        ...state,
        [listId]: { ...state[listId], cards: [...(state[listId].cards || []), cardId] }
      };
    }
    case "MOVE_CARD": {
      const { oldCardIndex, newCardIndex, sourceListId, destListId } = action.payload;
      // Check if lists exist
      if (!state[sourceListId] || !state[destListId]) {
        console.warn(`MOVE_CARD: Source or destination list not found`);
        return state;
      }
      // Move within the same list
      if (sourceListId === destListId) {
        const newCards = Array.from(state[sourceListId].cards || []);
        const [removedCard] = newCards.splice(oldCardIndex, 1);
        newCards.splice(newCardIndex, 0, removedCard);
        return {
          ...state,
          [sourceListId]: { ...state[sourceListId], cards: newCards }
        };
      }
      // Move card from one list to another
      const sourceCards = Array.from(state[sourceListId].cards || []);
      const [removedCard] = sourceCards.splice(oldCardIndex, 1);
      const destinationCards = Array.from(state[destListId].cards || []);
      destinationCards.splice(newCardIndex, 0, removedCard);
      return {
        ...state,
        [sourceListId]: { ...state[sourceListId], cards: sourceCards },
        [destListId]: { ...state[destListId], cards: destinationCards }
      };
    }
    case "DELETE_CARD": {
      const { cardId: newCardId, listId } = action.payload;
      // Check if list exists
      if (!state[listId]) {
        console.warn(`DELETE_CARD: List ${listId} not found in state`);
        return state;
      }
      return {
        ...state,
        [listId]: {
          ...state[listId],
          cards: (state[listId].cards || []).filter(cardId => cardId !== newCardId)
        }
      };
    }
    case "ADD_LIST": {
      const { listId, listTitle } = action.payload;
      return {
        ...state,
        [listId]: { _id: listId, title: listTitle, cards: [] }
      };
    }
    case "CHANGE_LIST_TITLE": {
      const { listId, listTitle } = action.payload;
      return {
        ...state,
        [listId]: { ...state[listId], title: listTitle }
      };
    }
    case "DELETE_LIST": {
      const { listId } = action.payload;
      const { [listId]: deletedList, ...restOfLists } = state;
      return restOfLists;
    }
    case "SET_LIST_SPRINT": {
      const { listId, sprintId } = action.payload;
      if (!state[listId]) {
        console.warn(`SET_LIST_SPRINT: List ${listId} not found in state`);
        return state;
      }
      return {
        ...state,
        [listId]: { ...state[listId], sprintId: sprintId || undefined }
      };
    }
    default:
      return state;
  }
};

export default listsById;
